import { Injectable } from '@angular/core';
import { AssetTypeMapperService } from '@angular-workspace/core';

@Injectable({
  providedIn: 'root'
})
export class HelperService {

  constructor(private assetTypeMapper: AssetTypeMapperService) { }

  /**
   * Map display asset type to API format
   * @param displayType Display name (e.g., "Dataset", "Model", "App", "Use Case")
   * @returns API format (e.g., "adex:DataBank", "adex:AiModel", "adex:Apps", "adex:UseCase")
   */
  mapAssetTypeToApiFormat(displayType: string): string {
    return this.assetTypeMapper.mapAssetTypeToApiFormat(displayType);
  }

  /**
   * Scroll to an element by ID
   * @param elementId The ID of the element to scroll to
   * @param behavior Scroll behavior ('smooth' | 'auto')
   * @param block Vertical alignment ('start' | 'center' | 'end' | 'nearest')
   */
  scrollToElement(
    elementId: string, 
    behavior: ScrollBehavior = 'smooth', 
    block: ScrollLogicalPosition = 'start'
  ): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior, block });
    }
  }

  /**
   * Check if a value is null or undefined
   * @param value The value to check
   * @returns True if the value is null or undefined
   */
  isNullOrUndefined(value: unknown): value is null | undefined {
    return value === null || value === undefined;
  }

  /**
   * Check if a string is empty or whitespace
   * @param value The string to check
   * @returns True if the string is empty or only whitespace
   */
  isEmptyString(value: string | null | undefined): boolean {
    return !value || value.trim().length === 0;
  }

  /**
   * Deep clone an object
   * @param obj The object to clone
   * @returns A deep copy of the object
   */
  deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  /**
   * Generate a unique ID
   * @param prefix Optional prefix for the ID
   * @returns A unique string ID
   */
  generateUniqueId(prefix: string = 'id'): string {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Debounce a function
   * @param func The function to debounce
   * @param delay Delay in milliseconds
   * @returns Debounced function
   */
  debounce<T extends (...args: any[]) => any>(
    func: T,
    delay: number
  ): (...args: Parameters<T>) => void {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }

  private static readonly PENDING_PUBLISH_STATES = [
    'pending',
    'under_review',
    'in_review',
    'review',
    'submitted',
    'awaiting',
  ];

  private static readonly REJECTED_PUBLISH_STATES = ['declined', 'rejected', 'denied'];

  private static readonly REJECTED_ITEM_STATES = ['declined', 'rejected', 'denied'];

  /**
   * itemStatus values that mean org-admin has not yet approved the asset
   * (includes SFTP/API compound statuses).
   */
  private static readonly UNVERIFIED_ITEM_PREFIX = 'UNVERIFIED';

  /** Resolve publish/review status from common API response shapes. */
  extractPublishStatus(item: unknown): string {
    if (!item || typeof item !== 'object') {
      return '';
    }

    const data = item as Record<string, unknown>;
    return String(
      data['publishStatus'] ??
        data['publish_status'] ??
        data['status'] ??
        data['reviewStatus'] ??
        data['approvalStatus'] ??
        ''
    ).trim();
  }

  /** Resolve itemStatus from common API response shapes. */
  extractItemStatus(item: unknown): string {
    if (!item || typeof item !== 'object') {
      return '';
    }

    const data = item as Record<string, unknown>;
    return String(data['itemStatus'] ?? data['item_status'] ?? '').trim();
  }

  /** True when publish status indicates pending / under-review. */
  isPublishStatusPending(item: unknown): boolean {
    const raw = this.extractPublishStatus(item).toLowerCase();
    if (!raw) {
      return false;
    }

    return HelperService.PENDING_PUBLISH_STATES.some((state) => raw.includes(state));
  }

  /** True when publish status indicates rejected / declined (Platform Admin). */
  isPublishStatusRejected(item: unknown): boolean {
    const raw = this.extractPublishStatus(item).toLowerCase();
    if (!raw) {
      return false;
    }

    return HelperService.REJECTED_PUBLISH_STATES.some((state) => raw.includes(state));
  }

  /** True when itemStatus indicates rejected / declined (Organisation Admin). */
  isItemStatusRejected(item: unknown): boolean {
    const raw = this.extractItemStatus(item).toLowerCase();
    if (!raw) {
      return false;
    }

    return HelperService.REJECTED_ITEM_STATES.some((state) => raw.includes(state));
  }

  /**
   * True when itemStatus is UNVERIFIED / UNVERIFIED-SFTP / UNVERIFIED-API —
   * i.e. not yet approved by the Organisation Admin.
   */
  isItemStatusUnverified(item: unknown): boolean {
    const raw = this.extractItemStatus(item).toUpperCase().trim();
    if (!raw) {
      return false;
    }

    return (
      raw === HelperService.UNVERIFIED_ITEM_PREFIX ||
      raw.startsWith(`${HelperService.UNVERIFIED_ITEM_PREFIX}-`)
    );
  }

  /** True when itemStatus is VERIFIED / VERIFIED-SFTP / VERIFIED-API. */
  isItemStatusVerified(item: unknown): boolean {
    const raw = this.extractItemStatus(item).toUpperCase().trim();
    return raw === 'VERIFIED' || raw.startsWith('VERIFIED-');
  }

  /** True when publish status indicates the asset is approved for catalogue interactions. */
  isPublishStatusApproved(item: unknown): boolean {
    const raw = this.extractPublishStatus(item).toUpperCase().trim();
    if (!raw) {
      return false;
    }

    return (
      raw === 'ACTIVE' ||
      raw === 'APPROVED' ||
      raw === 'GRANTED' ||
      raw === 'PUBLISHED' ||
      raw === 'LIVE'
    );
  }

  /** True when catalogue item marks data upload/processing as complete. */
  isDataUploadComplete(item: unknown): boolean {
    if (!item || typeof item !== 'object') {
      return false;
    }

    const data = item as Record<string, unknown>;
    const raw =
      data['dataUploadStatus'] ?? data['data_upload_status'] ?? data['uploadStatus'];
    return raw === true || String(raw).toLowerCase() === 'true';
  }

  /** True when dataUploadStatus is explicitly false (upload/processing incomplete). */
  isDataUploadStatusFalse(item: unknown): boolean {
    if (!item || typeof item !== 'object') {
      return false;
    }

    const data = item as Record<string, unknown>;
    const raw =
      data['dataUploadStatus'] ?? data['data_upload_status'] ?? data['uploadStatus'];
    if (raw == null) {
      return false;
    }
    return raw === false || String(raw).toLowerCase() === 'false';
  }

  /** Normalize industry from catalogue or user-interaction asset payloads. */
  extractIndustry(item: unknown): string {
    if (!item || typeof item !== 'object') {
      return '';
    }

    const raw = (item as Record<string, unknown>)['industry'];
    if (raw == null || raw === '') {
      return '';
    }

    if (Array.isArray(raw)) {
      return raw
        .filter((value) => value !== null && value !== undefined && value !== '')
        .map((value) => String(value).trim())
        .filter(Boolean)
        .join(', ');
    }

    if (typeof raw === 'string' && raw.trim() !== '') {
      return raw.trim();
    }

    return '';
  }

  /**
   * Extract and map access policy/status to display badge text
   * Maps: "Restricted" → "Restricted", "Open" → "Public"
   * @param item API item with accessPolicy, publishStatus, or status field
   * @returns Formatted badge text
   */
  extractAccessBadge(item: any): string {
    // Check accessPolicy first
    if (item.accessPolicy) {
      const policy = item.accessPolicy.toLowerCase();
      if (policy === 'restricted') {
        return 'Restricted';
      } else if (policy === 'open') {
        return 'Public';
      }
      // For any other accessPolicy value, capitalize first letter
      return item.accessPolicy.charAt(0).toUpperCase() + item.accessPolicy.slice(1).toLowerCase();
    }
    
    // Fallback to publishStatus
    if (item.publishStatus) {
      return item.publishStatus.charAt(0).toUpperCase() + item.publishStatus.slice(1).toLowerCase();
    }
    
    // Fallback to status
    if (item.status) {
      return item.status.charAt(0).toUpperCase() + item.status.slice(1).toLowerCase();
    }
    
    // Default to Public
    return 'Public';
  }

  /**
   * Get badge color variant based on access level
   * @param badge Badge text (e.g., "Public", "Restricted", "Private")
   * @returns Badge color variant
   */
  getBadgeColorVariant(badge: string): 'public' | 'restricted' | 'private' {
    const lowerBadge = badge.toLowerCase();
    if (lowerBadge === 'restricted') return 'restricted';
    if (lowerBadge === 'private') return 'private';
    return 'public';
  }
}

